import React, { useEffect, useMemo, useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { BalanceAdjustment, BudgetItem, ExpenseRule, IncomeRule, Plan, RepaymentEffect, ShopMonth } from '../types'
import { accountState, isExampleItem } from '../lib/storage'
import { ApiError, api } from '../lib/api'
import { rowsFromPdf } from '../lib/pdfImport'
import { namesToShare, rememberSharing, sharingPreferred } from '../lib/merchantNames'
import { rememberSpendingShare, shopMonthsFrom, spendingSharePreferred, spendingToShare } from '../lib/spending'
import { formatDateShort, formatMoney, formatSigned } from '../lib/format'
import { fromISODate } from '../lib/dateRules'
import { summarizeRule } from '../lib/cashflowEngine'
import { repaymentEffectBlocker } from '../lib/loanEngine'
import { RecurringSuggestion, detectRecurring, mergeStatements, toRule } from '../lib/recurringDetect'
import {
  AccountDetails,
  ColumnMapping,
  DateFormat,
  ParsedStatement,
  StatementRow,
  detectLayout,
  guessDateFormat,
  parseCsv,
  readRows,
  splitStatement,
} from '../lib/csvImport'
import {
  ImportChanges,
  LOAN_ACTIONS,
  LoanActionKind,
  OpeningCheck,
  ReviewRow,
  RowAction,
  activeLoans,
  buildImport,
  capitalPreview,
  checkOpening,
  expectedVsActual,
  loanOptionLabel,
  optionLabel,
  payoffPreview,
  reconcileAdjustment,
  reconcileWith,
  reviewRows,
  withImport,
} from '../lib/statementImport'

export type ImportPayload = ImportChanges & {
  newIncomes?: IncomeRule[]
  newExpenses?: ExpenseRule[]
  replaceExamples?: boolean
  adjustment?: BalanceAdjustment
  shopSpending?: ShopMonth[]
  statementFingerprint?: string
}

/** One or more statements, read and merged. */
interface ReadStatements {
  bank: string | null
  files: number
  rows: StatementRow[]
  unreadable: number
  balanceMismatches?: number
  /** The fingerprint of the account details the statements print, when they print any (SM-25). */
  fingerprint?: string
  /** The account those details matched, when that meant choosing it instead. */
  matched?: string
}

/** How the statements' account details fit the chosen account. */
type FingerprintFit = 'match' | 'mismatch' | 'new' | null

type Added = (IncomeRule | ExpenseRule)[]

interface Props {
  plan: Plan
  /** The account in view; the first account when looking at all of them. */
  defaultAccountId: string
  onApply: (accountId: string, changes: ImportPayload) => void
  onClose: () => void
}

type Step =
  | { kind: 'choose'; error?: string }
  | { kind: 'map'; statement: ParsedStatement; mapping: ColumnMapping }
  | { kind: 'reading' }
  | ({ kind: 'regulars'; suggestions: RecurringSuggestion[] } & ReadStatements)
  | ({ kind: 'review'; added: Added } & ReadStatements)

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name)

/** Statements without a header row (some HSBC exports) get numbered columns to map instead. */
function withHeaders(cells: string[][]): ParsedStatement {
  return (
    splitStatement(cells) ?? {
      headers: cells[0].map((_, i) => `Column ${i + 1}`),
      rows: cells,
    }
  )
}

type Read = Omit<ReadStatements, 'files' | 'fingerprint' | 'matched'> & { account?: AccountDetails }

/** The account details every statement that has any agrees on. */
function sharedDetails(read: Read[]): AccountDetails | undefined {
  const found = new Map(read.flatMap((r) => (r.account ? [[`${r.account.sortCode}:${r.account.accountNumber}`, r.account]] : [])))
  return found.size === 1 ? [...found.values()][0] : undefined
}

/** Best effort: without it, the opening balance is checked instead. */
function fingerprintOf(details: AccountDetails | undefined): Promise<string | undefined> {
  if (!details) return Promise.resolve(undefined)
  return api
    .statementFingerprint(details)
    .then((r) => r.fingerprint)
    .catch(() => undefined)
}

/** One file's rows, or a CSV whose columns need matching up first. */
async function readFile(file: File): Promise<Read | { statement: ParsedStatement } | { error: string }> {
  if (isPdf(file)) {
    // Read on the server: there's no PDF text extraction in the browser.
    return api
      .readPdfStatement(file)
      .then((statement) => ({ bank: statement.bank, ...rowsFromPdf(statement) }))
      .catch((err: unknown) => ({ error: err instanceof ApiError ? err.message : `${file.name} couldn't be read.` }))
  }
  const cells = parseCsv(await file.text())
  if (cells.length === 0) return { error: `${file.name} is empty, or isn't a CSV.` }
  const statement = withHeaders(cells)
  const detected = detectLayout(statement)
  return detected ? { bank: detected.bank, ...readRows(statement, detected.mapping) } : { statement }
}

export default function ImportStatementModal({ plan, defaultAccountId, onApply, onClose }: Props) {
  const [accountId, setAccountId] = useState(defaultAccountId)
  const [step, setStep] = useState<Step>({ kind: 'choose' })
  const account = plan.accounts.find((a) => a.id === accountId) ?? plan.accounts[0]
  const state = useMemo(() => accountState(plan, account), [plan, account])

  const fingerprint = step.kind === 'review' ? step.fingerprint : undefined
  const fingerprintFit: FingerprintFit = !fingerprint
    ? null
    : account.statementFingerprint === fingerprint
      ? 'match'
      : account.statementFingerprint
        ? 'mismatch'
        : 'new'
  // The balance is the fallback, for statements whose account details settle nothing.
  const opening = useMemo(
    () => (step.kind === 'review' && (fingerprintFit === null || fingerprintFit === 'new') ? checkOpening(plan, account.id, step.rows) : null),
    [step, fingerprintFit, plan, account.id],
  )

  /**
   * Into the account whose details the statements print, if one has them.
   * Then pay and bills to offer first, when the statements show any; straight
   * to the rows otherwise.
   */
  async function afterReading(read: ReadStatements, details: AccountDetails | undefined) {
    const fingerprint = await fingerprintOf(details)
    const owner = fingerprint ? plan.accounts.find((a) => a.statementFingerprint === fingerprint) : undefined
    const target = owner ?? account
    if (target.id !== account.id) setAccountId(target.id)
    const targetState = accountState(plan, target)
    const full: ReadStatements = { ...read, fingerprint, matched: target.id !== account.id ? target.name : undefined }
    const suggestions = detectRecurring(targetState, reviewRows(targetState, read.rows))
    setStep(suggestions.length > 0 ? { kind: 'regulars', suggestions, ...full } : { kind: 'review', added: [], ...full })
  }

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])]
    if (files.length === 0) return
    setStep({ kind: 'reading' })
    Promise.all(files.map(readFile))
      .then((results) => {
        const failed = results.find((r) => 'error' in r)
        if (failed) return setStep({ kind: 'choose', error: failed.error })
        const unmapped = results.findIndex((r) => 'statement' in r)
        if (unmapped !== -1) {
          const r = results[unmapped] as { statement: ParsedStatement }
          if (files.length === 1) return setStep({ kind: 'map', statement: r.statement, mapping: initialMapping(r.statement) })
          return setStep({
            kind: 'choose',
            error: `${files[unmapped].name} has a layout we don't recognise. Import it on its own to say which column is which.`,
          })
        }
        const read = results as Read[]
        const banks = [...new Set(read.map((r) => r.bank).filter((b): b is string => b !== null))]
        return afterReading(
          {
            bank: banks.length > 0 ? banks.join(' and ') : null,
            files: files.length,
            rows: mergeStatements(read.map((r) => r.rows)),
            unreadable: read.reduce((n, r) => n + r.unreadable, 0),
            balanceMismatches: read.reduce((n, r) => n + (r.balanceMismatches ?? 0), 0),
          },
          sharedDetails(read),
        )
      })
      .catch(() => setStep({ kind: 'choose', error: "Those files couldn't be read." }))
  }

  return (
    <Modal title="Import a bank statement" onClose={onClose} width="max-w-3xl">
      {step.kind === 'choose' && (
        <div>
          <p className="text-sm text-ink/80 mb-4">
            Download a statement from your bank as a CSV file, then choose it here. HSBC, Starling, Lloyds and
            Co-operative Bank statements can be a PDF instead. Choose two or three months at once and we can spot
            your pay and regular bills. Nothing changes until you've reviewed every row.
          </p>
          {plan.accounts.length > 1 && (
            <Field label="Account it's for">
              <select className={inputClass} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {plan.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Statement files (CSV or PDF), a month or more">
            <input
              className={inputClass}
              type="file"
              multiple
              accept=".csv,text/csv,.pdf,application/pdf"
              onChange={handleFiles}
            />
          </Field>
          {step.error && (
            <p role="alert" className="text-sm text-rag-bad">
              {step.error}
            </p>
          )}
        </div>
      )}

      {step.kind === 'reading' && (
        <p role="status" className="text-sm text-ink/80">
          Reading your statements…
        </p>
      )}

      {step.kind === 'map' && (
        <MapColumns
          statement={step.statement}
          mapping={step.mapping}
          onChange={(mapping) => setStep({ ...step, mapping })}
          onBack={() => setStep({ kind: 'choose' })}
          onContinue={() => {
            const { account: details, ...read } = readRows(step.statement, step.mapping)
            setStep({ kind: 'reading' })
            void afterReading({ bank: null, files: 1, ...read }, details)
          }}
        />
      )}

      {step.kind === 'regulars' && (
        <Regulars
          suggestions={step.suggestions}
          examples={[...state.incomes, ...state.expenses, ...state.budgets].filter(isExampleItem).map((i) => i.name)}
          currencySymbol={plan.settings.currencySymbol}
          onBack={() => setStep({ kind: 'choose' })}
          onContinue={(added) => setStep({ ...step, kind: 'review', added })}
        />
      )}

      {step.kind === 'review' && (
        <Review
          key={accountId}
          state={state}
          accountName={account.name}
          bank={step.bank}
          files={step.files}
          added={step.added}
          rows={step.rows}
          unreadable={step.unreadable}
          balanceMismatches={step.balanceMismatches ?? 0}
          matched={step.matched}
          fingerprintFit={fingerprintFit}
          opening={opening}
          onSwitchAccount={setAccountId}
          currencySymbol={plan.settings.currencySymbol}
          onBack={() => setStep({ kind: 'choose' })}
          // A mismatch never overwrites the details an account already has.
          onApply={(changes) =>
            onApply(account.id, fingerprintFit === 'new' ? { ...changes, statementFingerprint: fingerprint } : changes)
          }
        />
      )}
    </Modal>
  )
}

/** A first guess for an unknown layout: columns named like what they hold. */
function initialMapping(statement: ParsedStatement): ColumnMapping {
  const find = (re: RegExp) => statement.headers.findIndex((h) => re.test(h))
  const date = Math.max(0, find(/date/i))
  const description = find(/desc|detail|narrative|memo|payee|name|reference/i)
  const amount = find(/^amount|value/i)
  const paidIn = find(/paid in|credit|money in/i)
  const paidOut = find(/paid out|debit|money out/i)
  const balance = find(/balance/i)
  const split = amount === -1 && paidIn !== -1 && paidOut !== -1
  return {
    date,
    description: [description === -1 ? Math.min(1, statement.headers.length - 1) : description],
    ...(split ? { paidIn, paidOut } : { amount: amount === -1 ? statement.headers.length - 1 : amount }),
    balance: balance === -1 ? undefined : balance,
    dateFormat: guessDateFormat(statement.rows.map((r) => r[date] ?? '')),
  }
}

function MapColumns({
  statement,
  mapping,
  onChange,
  onBack,
  onContinue,
}: {
  statement: ParsedStatement
  mapping: ColumnMapping
  onChange: (m: ColumnMapping) => void
  onBack: () => void
  onContinue: () => void
}) {
  const split = mapping.amount === undefined
  const preview = readRows({ ...statement, rows: statement.rows.slice(0, 3) }, mapping).rows
  const column = (label: string, value: number | undefined, set: (i: number | undefined) => void, optional = false) => (
    <Field label={label}>
      <select
        className={inputClass}
        value={value ?? ''}
        onChange={(e) => set(e.target.value === '' ? undefined : Number(e.target.value))}
      >
        {optional && <option value="">None</option>}
        {statement.headers.map((h, i) => (
          <option key={i} value={i}>
            {h}
          </option>
        ))}
      </select>
    </Field>
  )

  return (
    <div>
      <p className="text-sm text-ink/80 mb-4">
        This statement's layout isn't one we recognise. Say which column is which, and check the preview below.
      </p>
      <div className="grid sm:grid-cols-2 gap-x-3">
        {column('Date', mapping.date, (i) => onChange({ ...mapping, date: i ?? 0 }))}
        <Field label="Date format">
          <select
            className={inputClass}
            value={mapping.dateFormat}
            onChange={(e) => onChange({ ...mapping, dateFormat: e.target.value as DateFormat })}
          >
            <option value="dmy">Day first (31/12/2026)</option>
            <option value="ymd">Year first (2026-12-31)</option>
            <option value="mdy">Month first (12/31/2026)</option>
          </select>
        </Field>
        {column('Description', mapping.description[0], (i) => onChange({ ...mapping, description: [i ?? 0] }))}
        {column('Balance (optional)', mapping.balance, (i) => onChange({ ...mapping, balance: i }), true)}
      </div>
      <Field label="Amounts">
        <select
          className={inputClass}
          value={split ? 'split' : 'single'}
          onChange={(e) =>
            onChange(
              e.target.value === 'split'
                ? { ...mapping, amount: undefined, paidIn: mapping.paidIn ?? 0, paidOut: mapping.paidOut ?? 0 }
                : { ...mapping, amount: mapping.amount ?? 0, paidIn: undefined, paidOut: undefined },
            )
          }
        >
          <option value="single">One column, negative for money out</option>
          <option value="split">Separate columns for money in and out</option>
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-x-3">
        {split ? (
          <>
            {column('Money in', mapping.paidIn, (i) => onChange({ ...mapping, paidIn: i }))}
            {column('Money out', mapping.paidOut, (i) => onChange({ ...mapping, paidOut: i }))}
          </>
        ) : (
          column('Amount', mapping.amount, (i) => onChange({ ...mapping, amount: i ?? 0 }))
        )}
      </div>

      <h3 className="text-xs uppercase tracking-wide text-ink/70 mt-2 mb-1">Preview</h3>
      {preview.length === 0 ? (
        <p className="text-sm text-rag-bad mb-4">None of the first rows can be read with these columns yet.</p>
      ) : (
        <ul className="text-sm border-y border-line divide-y divide-line mb-4">
          {preview.map((r, i) => (
            <li key={i} className="py-1.5 flex justify-between gap-3">
              <span className="text-ink/70 shrink-0">{r.date}</span>
              <span className="truncate flex-1">{r.description}</span>
              <span className="font-mono font-tabular">{formatSigned(r.amount)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex justify-between pt-3 border-t border-line">
        <button type="button" onClick={onBack} className="text-sm text-ink/70 hover:underline">
          Choose another file
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={preview.length === 0}
          className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  )
}

/**
 * Pay and bills the statements show, to tick before the rows: what makes a
 * plan from a statement rather than just a record of it (SM-30).
 */
function Regulars({
  suggestions,
  examples,
  currencySymbol,
  onBack,
  onContinue,
}: {
  suggestions: RecurringSuggestion[]
  /** Names of the starter plan's untouched examples, which adding anything here replaces. */
  examples: string[]
  currencySymbol: string
  onBack: () => void
  onContinue: (added: Added) => void
}) {
  // Seen twice or more starts ticked; a single Direct Debit is a guess, so it waits to be asked.
  const [picked, setPicked] = useState(() => new Set(suggestions.filter((s) => !s.seenOnce).map((s) => s.key)))
  const [edits, setEdits] = useState<Record<string, { name?: string; amount?: string }>>({})
  const [cleanNames, setCleanNames] = useState<Record<string, string>>({})

  useEffect(() => {
    const keys = [...new Set(suggestions.map((s) => s.merchant).filter((k) => k.length >= 3))]
    if (keys.length === 0) return
    let current = true
    api
      .merchantSuggestions(keys)
      .then(({ suggestions: names }) => current && setCleanNames(names))
      .catch(() => {})
    return () => {
      current = false
    }
  }, [suggestions])

  const nameOf = (s: RecurringSuggestion) => edits[s.key]?.name ?? cleanNames[s.merchant] ?? s.name
  const amountOf = (s: RecurringSuggestion) => edits[s.key]?.amount ?? String(s.amount)
  const valid = (s: RecurringSuggestion) => nameOf(s).trim() !== '' && Number(amountOf(s)) > 0
  const chosen = suggestions.filter((s) => picked.has(s.key))
  const edit = (s: RecurringSuggestion, change: { name?: string; amount?: string }) =>
    setEdits({ ...edits, [s.key]: { ...edits[s.key], ...change } })
  function toggle(key: string, on: boolean) {
    const next = new Set(picked)
    if (on) next.add(key)
    else next.delete(key)
    setPicked(next)
  }

  const section = (kind: 'income' | 'expense', title: string) => {
    const list = suggestions.filter((s) => s.kind === kind)
    if (list.length === 0) return null
    return (
      <>
        <h3 className="text-xs uppercase tracking-wide text-ink/70 mt-3 mb-1">{title}</h3>
        <ul className="border-y border-line divide-y divide-line">
          {list.map((s) => (
            <li key={s.key} className="py-2 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1">
              <input
                type="checkbox"
                aria-label={`Add ${nameOf(s)}`}
                checked={picked.has(s.key)}
                onChange={(e) => toggle(s.key, e.target.checked)}
              />
              <input
                aria-label={`Name for ${s.name}`}
                className={`${inputClass} py-1 flex-1 min-w-[8rem]`}
                value={nameOf(s)}
                onChange={(e) => edit(s, { name: e.target.value })}
              />
              <span className="text-xs text-ink/70 w-full sm:w-40 order-last sm:order-none">
                {summarizeRule(toRule(s))}
                {s.seenOnce && ' · seen once'}
                {s.variable && ' · varies'}
              </span>
              <label className="flex items-center gap-1 text-sm">
                <span className="text-ink/70">{currencySymbol}</span>
                <input
                  aria-label={`Amount for ${s.name}`}
                  className={`${inputClass} py-1 w-24 text-right font-mono font-tabular`}
                  inputMode="decimal"
                  value={amountOf(s)}
                  onChange={(e) => edit(s, { amount: e.target.value })}
                />
              </label>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <div>
      <p className="text-sm text-ink/80">
        These look like regular payments. Tick the ones to add to your plan, and fix any name or amount that's off.
        You can change the details later.
      </p>
      {examples.length > 0 && (
        <p className="text-xs text-ink/70 mt-1">
          Adding them takes out the example {listOf(examples)} your plan started with.
        </p>
      )}
      {section('income', 'Pay and money in')}
      {section('expense', 'Bills and regular payments')}
      <div className="flex items-center justify-between pt-3 mt-4 border-t border-line">
        <button type="button" onClick={onBack} className="text-sm text-ink/70 hover:underline">
          Choose other files
        </button>
        <button
          type="button"
          disabled={!chosen.every(valid)}
          onClick={() =>
            onContinue(chosen.map((s) => ({ ...toRule(s), name: nameOf(s).trim(), amount: Math.abs(Number(amountOf(s))) })))
          }
          className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {chosen.length === 0 ? 'Skip, go to the rows' : `Add ${chosen.length} and go to the rows`}
        </button>
      </div>
    </div>
  )
}

function listOf(names: string[]): string {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function actionValue(a: RowAction): string {
  if (a.kind === 'actual') return `actual:${a.sourceId}`
  if ('loanId' in a) return `${a.kind}:${a.loanId}`
  return a.kind
}

const EFFECTS: { effect: RepaymentEffect; label: string }[] = [
  { effect: 'shorten', label: 'Shorten the term' },
  { effect: 'lower', label: 'Lower the payment' },
]

/** The loan action an option value like `payoff:<loanId>` stands for, if it is one. */
function loanAction(value: string): { kind: LoanActionKind; loanId: string } | null {
  const action = LOAN_ACTIONS.find((a) => value.startsWith(`${a.kind}:`))
  return action ? { kind: action.kind, loanId: value.slice(action.kind.length + 1) } : null
}

function Review({
  state: before,
  accountName,
  bank,
  files,
  added,
  rows,
  unreadable,
  balanceMismatches,
  matched,
  fingerprintFit,
  opening,
  onSwitchAccount,
  currencySymbol,
  onBack,
  onApply,
}: {
  state: ReturnType<typeof accountState>
  accountName: string
  bank: string | null
  files: number
  /** Ticked in the step before: rows they cover show as already in the plan. */
  added: Added
  rows: StatementRow[]
  unreadable: number
  balanceMismatches: number
  /** The account chosen because the statement's account details matched it. */
  matched?: string
  fingerprintFit: FingerprintFit
  /** Set when the opening balance is far from what this account expects. */
  opening: OpeningCheck | null
  onSwitchAccount: (accountId: string) => void
  currencySymbol: string
  onBack: () => void
  onApply: (changes: ImportPayload) => void
}) {
  const newIncomes = added.filter((a): a is IncomeRule => a.kind === 'income')
  const newExpenses = added.filter((a): a is ExpenseRule => a.kind === 'expense')
  // With pay and bills from the statement, the examples go (see APPLY_IMPORT).
  const replaceExamples = added.length > 0
  const state = useMemo(() => {
    const keep = <T extends IncomeRule | ExpenseRule | BudgetItem>(item: T) => !replaceExamples || !isExampleItem(item)
    return {
      ...before,
      incomes: [...before.incomes.filter(keep), ...added.filter((a): a is IncomeRule => a.kind === 'income')],
      expenses: [...before.expenses.filter(keep), ...added.filter((a): a is ExpenseRule => a.kind === 'expense')],
      budgets: before.budgets.filter(keep),
    }
  }, [before, added, replaceExamples])
  const reviewed = useMemo(() => reviewRows(state, rows), [state, rows])
  const [actions, setActions] = useState<Map<string, RowAction>>(new Map())
  const [remember, setRemember] = useState(true)
  const [share, setShare] = useState(sharingPreferred)
  // Opt-in, so it starts unticked unless this browser has said yes before (SM-18).
  const [wasSharingSpending] = useState(spendingSharePreferred)
  const [shareSpending, setShareSpending] = useState(wasSharingSpending)
  /** Clean names the shared catalogue has for these merchants — "Tesco" for "TESCO STORES 3125". */
  const [cleanNames, setCleanNames] = useState<Record<string, string>>({})
  const [reconcile, setReconcile] = useState(true)
  const [importAnyway, setImportAnyway] = useState(false)
  const mismatch = fingerprintFit === 'mismatch'

  const fresh = reviewed.filter((r) => r.status === 'new')
  const duplicates = reviewed.filter((r) => r.status === 'duplicate').length
  const beforeStart = reviewed.filter((r) => r.status === 'before-start').length
  const changes = useMemo(
    () => buildImport(state, reviewed, actions, remember, cleanNames),
    [state, reviewed, actions, remember, cleanNames],
  )

  // Ask once per statement. A failure just means the bank's own descriptions.
  useEffect(() => {
    const keys = [...new Set(reviewed.filter((r) => r.status === 'new' && r.merchant.length >= 3).map((r) => r.merchant))]
    if (keys.length === 0) return
    let current = true
    api
      .merchantSuggestions(keys)
      .then(({ suggestions }) => current && setCleanNames(suggestions))
      .catch(() => {})
    return () => {
      current = false
    }
  }, [reviewed])
  // Not for what may be another account's statement: the adjustment would cover the gap up.
  const offer = useMemo(
    () => (opening || mismatch ? null : reconcileWith(withImport(state, changes), rows)),
    [opening, mismatch, state, changes, rows],
  )
  const budgets = state.budgets.filter((b) => b.active)
  const bills = state.expenses.filter((e) => e.active)
  const incomes = state.incomes.filter((i) => i.active)
  const loans = activeLoans(state)
  const money = (n: number) => formatMoney(n, currencySymbol)

  const actionFor = (row: ReviewRow) => actions.get(row.importId) ?? row.suggested
  function setAction(row: ReviewRow, value: string) {
    const next = new Map(actions)
    const loan = loanAction(value)
    const was = actionFor(row)
    next.set(
      row.importId,
      value.startsWith('actual:')
        ? { kind: 'actual', sourceId: value.slice('actual:'.length) }
        : loan?.kind === 'capital'
          ? { ...loan, effect: (was.kind === 'capital' && was.effect) || 'shorten' }
          : (loan ??
            (value === 'covered' && row.suggested.kind === 'covered' ? row.suggested : { kind: value as 'oneoff' | 'skip' })),
    )
    setActions(next)
  }
  function setEffect(row: ReviewRow, effect: RepaymentEffect) {
    const was = actionFor(row)
    if (was.kind !== 'capital') return
    setActions(new Map(actions).set(row.importId, { ...was, effect }))
  }

  // Loan actions add a one-off linked to the loan; one whose loan is gone falls back to a plain one-off and counts as unplaced.
  const loanRecords = changes.oneOffs.filter((o) => o.loanId)
  const loanRows = fresh.filter((r) => 'loanId' in actionFor(r)).length
  const counts = {
    oneoff: changes.oneOffs.length - loanRecords.length,
    actual: fresh.filter((r) => actionFor(r).kind === 'actual').length - (changes.unplaced - (loanRows - loanRecords.length)),
    paidOff: loanRecords.filter((o) => fresh.some((r) => r.importId === o.importId && actionFor(r).kind === 'payoff')).length,
    capital: loanRecords.filter((o) => fresh.some((r) => r.importId === o.importId && actionFor(r).kind === 'capital')).length,
  }

  return (
    <div>
      <p className="text-sm text-ink/80 mb-1">
        {files > 1
          ? `Read ${files} ${bank ? `${bank} ` : ''}statements`
          : bank
            ? `Read as a ${bank} statement`
            : 'Read with the columns you chose'}
        , into <strong>{accountName}</strong>.
      </p>
      <ul className="text-xs text-ink/70 mb-3 space-y-0.5">
        {duplicates > 0 && <li>{duplicates} already imported — skipped.</li>}
        {beforeStart > 0 && <li>{beforeStart} from before this account's starting balance date — skipped.</li>}
        {unreadable > 0 && <li>{unreadable} rows couldn't be read (no date or amount).</li>}
      </ul>
      {balanceMismatches > 0 && (
        <p role="alert" className="text-xs text-ink border border-rag-medium bg-rag-mediumBg rounded-sm px-3 py-2 mb-3">
          {balanceMismatches === 1 ? 'One balance' : `${balanceMismatches} balances`} on the statement{' '}
          {balanceMismatches === 1 ? "doesn't" : "don't"} add up with the rows read before{' '}
          {balanceMismatches === 1 ? 'it' : 'them'}, so something may have been misread. Check the rows against
          your statement before importing.
        </p>
      )}

      {matched && !mismatch && (
        <p role="status" className="text-xs text-ink/80 mb-3">
          Matched to <strong>{matched}</strong> by its account details.
        </p>
      )}
      {mismatch && (
        <div role="alert" className="text-sm text-rag-bad bg-rag-badBg border border-rag-bad/20 rounded-sm px-3 py-2 mb-3">
          <p>
            This statement's sort code and account number aren't the ones <strong>{accountName}</strong>'s statements
            had before, so it's probably for another account. Check before importing.
          </p>
          <label className="flex items-start gap-2 mt-2 text-ink">
            <input type="checkbox" className="mt-1" checked={importAnyway} onChange={(e) => setImportAnyway(e.target.checked)} />
            Import anyway
          </label>
        </div>
      )}
      {opening && (
        <div role="alert" className="text-sm text-ink border border-rag-medium bg-rag-mediumBg rounded-sm px-3 py-2 mb-3">
          {opening.better ? (
            <>
              <p>
                This looks like your <strong>{opening.better.name}</strong> account's statement (balance{' '}
                {money(opening.statementOpening)} matches). Import into <strong>{accountName}</strong> anyway?
              </p>
              <button
                type="button"
                onClick={() => onSwitchAccount(opening.better!.accountId)}
                className="mt-2 px-3 py-1 text-sm rounded-sm border border-line bg-white hover:bg-line/30"
              >
                Switch to {opening.better.name}
              </button>
            </>
          ) : (
            <p>
              The opening balance ({money(opening.statementOpening)}) is far from what {accountName} expects on{' '}
              {formatDateShort(fromISODate(opening.date))} ({money(opening.expected)}). Check this is the right
              account.
            </p>
          )}
        </div>
      )}

      {fresh.length === 0 ? (
        <p className="text-sm text-ink/80 mb-4">
          {added.length > 0
            ? 'There are no rows from after the starting balance date to import.'
            : "There's nothing new to import from this statement."}
        </p>
      ) : (
        // One row per transaction: a card on a phone, a table row from sm up.
        <div className="max-h-[55vh] sm:max-h-[45vh] overflow-y-auto border-y border-line mb-3">
          <table className="block sm:table w-full text-sm">
            <thead className="hidden sm:table-header-group sticky top-0 bg-white text-xs text-ink/70">
              <tr>
                <th className="text-left font-normal py-1.5 pr-2">Date</th>
                <th className="text-left font-normal py-1.5 pr-2">Description</th>
                <th className="text-right font-normal py-1.5 pr-2">Amount</th>
                <th className="text-left font-normal py-1.5">Becomes</th>
              </tr>
            </thead>
            <tbody className="block sm:table-row-group divide-y divide-line">
              {fresh.map((row) => {
                const action = actionFor(row)
                const match = expectedVsActual(state, row, action)
                const settling = action.kind === 'payoff' ? loans.find((l) => l.id === action.loanId) : undefined
                const payoff = settling && row.amount < 0 ? payoffPreview(state, settling, row, currencySymbol) : null
                const repaying = action.kind === 'capital' && row.amount < 0 ? loans.find((l) => l.id === action.loanId) : undefined
                const effect = (action.kind === 'capital' && action.effect) || 'shorten'
                const blocked = repaying ? repaymentEffectBlocker(repaying, effect) : null
                return (
                  <tr key={row.importId} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 py-2.5 sm:table-row sm:py-0">
                    <td className="order-1 col-span-2 text-xs text-ink/70 whitespace-nowrap sm:order-none sm:py-1.5 sm:pr-2 sm:text-sm">{formatDateShort(fromISODate(row.date))}</td>
                    <td className="truncate font-medium sm:py-1.5 sm:pr-2 sm:max-w-[16rem] sm:font-normal" title={row.description}>
                      {cleanNames[row.merchant] ?? row.description}
                    </td>
                    <td className={`text-right sm:py-1.5 sm:pr-2 font-mono font-tabular whitespace-nowrap ${row.amount < 0 ? 'text-rag-bad' : 'text-rag-good'}`}>
                      {formatSigned(row.amount, currencySymbol)}
                    </td>
                    <td className="order-2 col-span-2 mt-1.5 sm:order-none sm:mt-0 sm:py-1.5">
                      <select
                        aria-label={`What ${row.description} on ${row.date} becomes`}
                        className={`${inputClass} py-1.5 sm:py-1 sm:max-w-[16rem]`}
                        value={actionValue(action)}
                        onChange={(e) => setAction(row, e.target.value)}
                      >
                        <option value="oneoff">One-off transaction</option>
                        {row.suggested.kind === 'covered' && (
                          <option value="covered">Already in plan: {row.suggested.name}</option>
                        )}
                        {row.amount > 0 && incomes.length > 0 && (
                          <optgroup label="Income actually received">
                            {incomes.map((i) => (
                              <option key={i.id} value={`actual:${i.id}`} title={i.name}>
                                {optionLabel(i, currencySymbol, row.date)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {budgets.length > 0 && (
                          <optgroup label="Spend against a budget">
                            {budgets.map((b) => (
                              <option key={b.id} value={`actual:${b.id}`} title={b.name}>
                                {optionLabel(b, currencySymbol, row.date)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {bills.length > 0 && (
                          <optgroup label="What a bill really came to">
                            {bills.map((b) => (
                              <option key={b.id} value={`actual:${b.id}`} title={b.name}>
                                {optionLabel(b, currencySymbol, row.date)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {row.amount < 0 &&
                          loans.length > 0 &&
                          LOAN_ACTIONS.map((a) => (
                            <optgroup key={a.kind} label={a.group}>
                              {loans.map((l) => (
                                <option key={l.id} value={`${a.kind}:${l.id}`} title={l.name}>
                                  {loanOptionLabel(l, currencySymbol)}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        <option value="skip">Skip</option>
                      </select>
                      {match?.source.kind === 'income' && (
                        <p className="text-xs text-ink/70 mt-0.5">
                          Expected {money(match.expected)} · got {money(match.got)}
                        </p>
                      )}
                      {repaying && (
                        <>
                          <select
                            aria-label={`What the repayment on ${row.date} does`}
                            className={`${inputClass} py-1.5 mt-1 sm:py-1 sm:max-w-[16rem]`}
                            value={effect}
                            onChange={(e) => setEffect(row, e.target.value as RepaymentEffect)}
                          >
                            {EFFECTS.map((o) => {
                              const reason = repaymentEffectBlocker(repaying, o.effect)
                              return (
                                <option key={o.effect} value={o.effect} disabled={reason !== null}>
                                  {reason ? `${o.label} (${reason.toLowerCase()})` : o.label}
                                </option>
                              )
                            })}
                          </select>
                          <p className="text-xs text-ink/70 mt-0.5">{capitalPreview(repaying, row, effect, currencySymbol)}</p>
                          {blocked && <p className="text-xs text-ink/70 mt-0.5">{blocked}: the term and payment stay as they are.</p>}
                        </>
                      )}
                      {payoff && <p className="text-xs text-ink/70 mt-0.5">{payoff.text}</p>}
                      {payoff?.warning && (
                        <p className="text-xs text-ink border border-rag-medium bg-rag-mediumBg rounded-sm px-2 py-1 mt-0.5 max-w-[16rem]">
                          {payoff.warning}
                        </p>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {fresh.length > 0 && budgets.length + bills.length + incomes.length > 0 && (
        <label className="flex items-start gap-2 text-sm text-ink/80 mb-2">
          <input type="checkbox" className="mt-1" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Remember which budget, bill or income each payee goes to, and suggest it next time
        </label>
      )}

      {fresh.length > 0 && (
        <label className="flex items-start gap-2 text-sm text-ink/80 mb-2">
          <input type="checkbox" className="mt-1" checked={share} onChange={(e) => setShare(e.target.checked)} />
          <span>
            Share merchant names with Snapshot Money to make automatic matching better in future
            <span className="block text-xs text-ink/70 mt-0.5">
              Only the name of each shop you match to a budget or bill is sent — no amounts, dates or anything
              else from your statement — and nothing that identifies you is saved with it.
            </span>
          </span>
        </label>
      )}

      {(fresh.length > 0 || wasSharingSpending) && (
        <label className="flex items-start gap-2 text-sm text-ink/80 mb-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={shareSpending}
            onChange={(e) => setShareSpending(e.target.checked)}
          />
          <span>
            Also share how much I spend at these shops each month (totals only, never single payments) to build
            anonymous averages
            <span className="block text-xs text-ink/70 mt-0.5">
              Only shops on the shared list count, and an average is only worked out from 25 or more people.
              Only people who share see how their own spending compares with the averages. Unticking this later
              deletes everything you've shared.
            </span>
          </span>
        </label>
      )}

      {wasSharingSpending && !shareSpending && (
        <div role="alert" className="text-xs text-ink border border-rag-medium bg-rag-mediumBg rounded-sm px-3 py-2 mb-2 ml-6">
          <strong className="font-medium">Importing with this unticked deletes the spending you've shared so far</strong>, every
          month of it, and it can't be brought back. You'll also stop seeing how your spending compares with the averages
          (you'll still see the averages themselves). Ticking it again later starts from your next import, and your
          comparison comes back with it. Your own plan isn't affected, and averages already worked out stay as they are,
          since they don't include anyone's own figures.
        </div>
      )}

      {offer && (
        <label className="flex items-start gap-2 text-sm text-ink/80 mb-2">
          <input type="checkbox" className="mt-1" checked={reconcile} onChange={(e) => setReconcile(e.target.checked)} />
          <span>
            Your statement says {money(offer.statementBalance)} on {formatDateShort(fromISODate(offer.date))}; the
            plan says {money(offer.projected)}
            {offer.stillToSpend > 0 ? `, not counting ${money(offer.stillToSpend)} of budgets still to spend` : ''}. Add a {formatSigned(offer.delta, currencySymbol)} balance adjustment
            so they match.
          </span>
        </label>
      )}

      {changes.unplaced > 0 && (
        <p className="text-xs text-ink/70 mb-2">
          {changes.unplaced} matched to a budget, bill or income with no occurrence near its date, or to a loan that's no
          longer active, so it's added as a one-off instead.
        </p>
      )}

      {changes.zeroed > 0 && (
        <p className="text-xs text-ink/70 mb-2">
          {changes.zeroed} budget{changes.zeroed === 1 ? '' : 's'} with nothing spent in this statement{' '}
          {changes.zeroed === 1 ? 'is' : 'are'} recorded as {money(0)} spent.
        </p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-line">
        <button type="button" onClick={onBack} className="text-sm text-ink/70 hover:underline">
          Choose another file
        </button>
        <button
          type="button"
          disabled={(fresh.length === 0 && !offer && added.length === 0) || (mismatch && !importAnyway)}
          onClick={() => {
            rememberSharing(share)
            if (share) {
              const names = namesToShare(reviewed, actionFor)
              // After the fact and best effort: the import doesn't wait on it.
              if (names.length > 0) api.shareMerchants(names).catch(() => {})
            }
            rememberSpendingShare(shareSpending)
            if (shareSpending) {
              const months = spendingToShare(reviewed, actionFor, new Set(Object.keys(cleanNames)))
              if (Object.keys(months).length > 0) api.shareSpending(months).catch(() => {})
            } else if (wasSharingSpending) {
              // Opting out takes back what was shared, not just what comes next.
              api.withdrawSpending().catch(() => {})
            }
            onApply({
              ...changes,
              newIncomes,
              newExpenses,
              replaceExamples,
              adjustment: offer && reconcile ? reconcileAdjustment(offer) : undefined,
              // Kept in the person's own plan whether or not they share, for comparing (SM-19).
              shopSpending: shopMonthsFrom(reviewed, actionFor),
            })
          }}
          className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {fresh.length === 0 && added.length > 0 ? (
            `Add ${added.length} regular payment${added.length === 1 ? '' : 's'}`
          ) : (
            `Import ${[
              counts.oneoff > 0 || counts.paidOff + counts.capital === 0 ? `${counts.oneoff} one-off${counts.oneoff === 1 ? '' : 's'}` : '',
              counts.actual > 0 ? `${counts.actual} actual${counts.actual === 1 ? '' : 's'}` : '',
              counts.paidOff > 0 ? `${counts.paidOff} loan${counts.paidOff === 1 ? '' : 's'} paid off` : '',
              counts.capital > 0 ? `${counts.capital} capital repayment${counts.capital === 1 ? '' : 's'}` : '',
              added.length > 0 ? `${added.length} regular payment${added.length === 1 ? '' : 's'}` : '',
            ]
              .filter(Boolean)
              .join(' and ')}`
          )}
        </button>
      </div>
    </div>
  )
}
